import { ILeaseTract } from 'src/models/dlcLease/ILeaseTract';

export interface ILeaseTractBonusSummary {
    totalBonus: number;
    totalNetAcres: number;
    bonusPerAcre: number | null;
}

export interface ILeaseTractTotals {
    net: number;
    gross: number;
    bonus: number;
    rental: number;
    fees: number;
}

const finiteNumberOrZero = (value?: number): number => {
    const numericValue = Number(value);
    return isFinite(numericValue) ? numericValue : 0;
};

const getLogicalTractKey = (tract: ILeaseTract, index: number): string => {
    const identity = [tract.lsNo, tract.clNo, tract.prNo, tract.scNo, tract.trNo];
    if (identity.some((value) => value === undefined || value === null)) {
        return `row-${index}`;
    }

    return [...identity, (tract.trCno || '').trim().toUpperCase()].join('|');
};

const getQualifyingTractRows = (tracts?: ILeaseTract[]): ILeaseTract[] => {
    const activeTracts = (tracts || []).filter((tract) => tract && tract.operation !== 'delete');
    const groupedTracts = new Map<string, ILeaseTract[]>();

    activeTracts.forEach((tract, index) => {
        const key = getLogicalTractKey(tract, index);
        groupedTracts.set(key, [...(groupedTracts.get(key) || []), tract]);
    });

    return Array.from(groupedTracts.values()).reduce<ILeaseTract[]>((rows, group) => {
        const positiveNetRows = group.filter((tract) => finiteNumberOrZero(tract.net) > 0);
        const distinctNetValues = new Set(positiveNetRows.map((tract) => finiteNumberOrZero(tract.net)));
        const horizonNumbers = positiveNetRows.map((tract) => Number(tract.hzNo)).filter((horizonNumber) => isFinite(horizonNumber));

        if (distinctNetValues.size !== 1 || horizonNumbers.length === 0) {
            return [...rows, ...group];
        }

        const minimumHorizon = Math.min(...horizonNumbers);
        return [...rows, ...positiveNetRows.filter((tract) => Number(tract.hzNo) === minimumHorizon)];
    }, []);
};

export const calculateLeaseTractTotals = (tracts?: ILeaseTract[]): ILeaseTractTotals => {
    const qualifyingRows = getQualifyingTractRows(tracts);

    return qualifyingRows.reduce<ILeaseTractTotals>(
        (totals, tract) => ({
            net: totals.net + finiteNumberOrZero(tract.net),
            gross: totals.gross + finiteNumberOrZero(tract.gross),
            bonus: totals.bonus + finiteNumberOrZero(tract.bonus),
            rental: totals.rental + finiteNumberOrZero(tract.rental),
            fees: totals.fees + finiteNumberOrZero(tract.fees),
        }),
        { net: 0, gross: 0, bonus: 0, rental: 0, fees: 0 }
    );
};

export const calculateLeaseTractBonusSummary = (tracts?: ILeaseTract[]): ILeaseTractBonusSummary => {
    const totals = calculateLeaseTractTotals(tracts);

    return {
        totalBonus: totals.bonus,
        totalNetAcres: totals.net,
        bonusPerAcre: totals.net > 0 ? totals.bonus / totals.net : null,
    };
};

export const leaseBonusPerAcreDiffers = (calculated: number | null, master?: number): boolean => {
    if (master === undefined || master === null) {
        return false;
    }

    const numericMaster = Number(master);
    if (calculated === null || !isFinite(numericMaster)) {
        return false;
    }

    return Math.round(calculated * 100) !== Math.round(numericMaster * 100);
};



// WEBPACK FOOTER //
// ./src/components/leases/sections/leaseTractCalculations.ts